/**
 * 等保三级-接口报文加解密工具（前端配套实现）
 *
 * <p>算法组合：RSA-2048 公钥包裹随机 AES-256 会话密钥 + AES-256-CBC 加解密报文。
 * 后端配套实现见 bxzg_manager 的 ApiCryptoFilter / ApiCryptoUtils，双方报文约定：
 * <pre>
 *   AES 密文 = Base64(随机IV(16字节) + AES-256-CBC密文)
 *   请求头   X-Api-Enc = 1（加密标识）
 *   请求头   X-Api-Key = RSA公钥加密的 AES 会话密钥（Base64）
 *   请求体   = AES密文（明文为原表单串或 JSON 串）
 *   响应体   = {"encData": "AES密文"}（明文 JSON 用同一 AES 密钥加密）
 * </pre>
 *
 * <p>豁免规则：blob 下载（Excel 导出/文件预览）与 FormData 上传不加加密头，
 * 后端对未携带加密头的请求按明文兼容处理。
 *
 * <p>应急开关：本文件按构建环境选择公钥，公钥为空字符串时前端加密自动关闭
 * （需与后端 api.crypto.enabled=false 同步关闭）。
 */
import JSEncrypt from "jsencrypt";
import CryptoJS from "crypto-js";

/** AES-CBC 初始向量长度（字节），与后端 ApiCryptoUtils.IV_LENGTH 保持一致 */
const IV_LENGTH = 16;

/**
 * RSA 公钥（X.509 格式 Base64 单行，不含 PEM 头尾）
 * 三环境密钥对与后端 application-dev/pre/pro.properties 中 api.crypto.rsa-private-key 一一配对
 * 注意：更换密钥时前后端必须同步替换
 */
const PUBLIC_KEY_DEV = "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAhANPXDtQWWfcqE+I2sodqKrRod77i/9yx3K/CAVI+fXwJJwsMGfgU48Sgo8adFVXYlW9SKsTYshyqQCc2qFFOZOS7X/lOV/sqvOT64RFhXTpPnB16QA2hqHueVOQh8RcGzl5E44iKRRkMC4hYd0IqurB6cBej4QhTevmyZ/KiQylcJh7NrtS4RxJgkr5NHGNUvHUDK5QVXM4ZXCucQTnH00MNlLnm2kHVyhO5exv5JzG+TnT1UeQ02yIilUSjLjRCdCQJAlCLYJUlDAzwYXlecVuAucfqpjC+AWRKOT2OD5zZLD/JzidNzoW/IyeYAlS+NVPomO/Kf4ttJnlJzBNBQIDAQAB";
const PUBLIC_KEY_PRE = "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAqLRjv8/gjz1NTxY2gg2G27PDsXzMjSJsRlTZvdVDhd+eZ7NZSa/MMyhjhRTcxBU3G4c8W0gPwvsstD5JrjLl8pgotvVSD56ncsBYaJYBvwO26EMPW21kCCKWE1/8NsBRPy9E9nFbGmCWQmLtczCnEJdeiN0yGX51SXnAHgm8DigekTunYDeUAg7SO/XF1wr4r8k17zaXgtyX53VSApTho3Tch/zkjyLS+Vd9KttfL9EkFKcYHVw56o6fo/ogBiXj5/unrs7rUaY8mm1dCqU1s/x8LwPNc5GAodoC0vbPNrsrTJJ7Z442C1nsLAXnjx5tn54fkA+fFOU3lS18G5pwWwIDAQAB";
const PUBLIC_KEY_PRO = "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA5jDV8Sop+UMGMEfqT9U+cb8dzvm7DdXTkGoRc9D+4cMyeqQBJo2DQ92JUHiH80YcD+6XzPXx5wC8cvWQXLbc4W4j7IxlzqGyqpePPrbJiSWKz89Ekry/svSYWN89TyuatoOZ42C+iZ2SIyARh5NbpaQ9sC9n4J0mxVjciI9c4REgW6QDmHRz63uum89JNbaMHMMJxz2Jo45+6MVxJBWDGv2Gh21dkMPCZWOt1814Hzmm/wV4uZcQV7OGPuveXld88zm9zkHYbDNqjmr4B8nSkT+GzSx8OOFREqoMz8db2ExGYsP25bceJQj6ZvbffejuOzlnmpwHba+fgyNSl/25TwIDAQAB";

/**
 * 按构建环境选择公钥（判断逻辑与 common.js 的 url 环境切换保持一致）：
 * - npm run dev（NODE_ENV=development）→ 本地代理（后端 dev 配置）→ dev 密钥
 * - npm run testing（NODE_ENV=production 且 type=testing）→ 测试环境（后端 pre 配置）→ pre 密钥
 * - npm run build（NODE_ENV=production）→ 生产环境（后端 pro 配置）→ pro 密钥
 */
function resolvePublicKey() {
  if (process.env.NODE_ENV !== "production") {
    return PUBLIC_KEY_DEV;
  }
  return process.env.type === "testing" ? PUBLIC_KEY_PRE : PUBLIC_KEY_PRO;
}

/**
 * 生成随机 AES-256 会话密钥（32 字节，Base64 字符串）
 * 每次请求独立随机生成，不落盘、不重用
 */
function randomAesKey() {
  return CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Base64);
}

/**
 * AES-256-CBC 加密：输出 Base64(IV(16字节) + 密文)
 */
function aesEncrypt(plainText, aesKeyBase64) {
  const key = CryptoJS.enc.Base64.parse(aesKeyBase64);
  const iv = CryptoJS.lib.WordArray.random(IV_LENGTH);
  const encrypted = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(plainText), key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });
  // IV 前置拼接密文后再整体 Base64，后端取前 16 字节作 IV（与后端 aesDecryptFromBase64 约定一致）
  const combined = iv.clone().concat(encrypted.ciphertext);
  return combined.toString(CryptoJS.enc.Base64);
}

/**
 * AES-256-CBC 解密：入参 Base64(IV(16字节) + 密文)
 */
function aesDecrypt(base64IvAndCipher, aesKeyBase64) {
  const key = CryptoJS.enc.Base64.parse(aesKeyBase64);
  const all = CryptoJS.enc.Base64.parse(base64IvAndCipher);
  // 取前 4 个 32 位字（16 字节）作为 IV，其余为密文体
  const iv = CryptoJS.lib.WordArray.create(all.words.slice(0, 4), IV_LENGTH);
  const cipherWords = CryptoJS.lib.WordArray.create(
    all.words.slice(4),
    all.sigBytes - IV_LENGTH
  );
  const decrypted = CryptoJS.AES.decrypt(
    { ciphertext: cipherWords },
    key,
    { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
  );
  return decrypted.toString(CryptoJS.enc.Utf8);
}

/**
 * RSA 公钥加密（包裹 AES 会话密钥，后端用私钥解出）
 * 注：公钥以 PEM 格式喂给 jsencrypt，密钥对更换时只改上面的 Base64 常量
 */
function rsaEncrypt(plainText) {
  const pem =
    "-----BEGIN PUBLIC KEY-----\n" + resolvePublicKey() + "\n-----END PUBLIC KEY-----\n";
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(pem);
  return encrypt.encrypt(plainText);
}

/**
 * 请求拦截器加密入口（main.js 的 axios.interceptors.request 中调用）
 *
 * <p>处理规则：
 * <ul>
 *   <li>blob 下载 / FormData 上传：不加密，原样放行（后端按明文兼容处理）；</li>
 *   <li>普通 JSON/表单请求：随机生成 AES 密钥 → RSA 加密放请求头 → 报文体整体 AES 加密；</li>
 *   <li>AES 密钥挂在 config.__apiAesKey（axios 自定义字段不随请求发送），
 *       响应拦截器据此对响应解密。</li>
 * </ul>
 */
export function encryptRequest(config) {
  const publicKey = resolvePublicKey();
  // 公钥为空 = 前端加密开关关闭
  if (!publicKey) {
    return config;
  }
  // 文件下载（Excel 导出/文件预览）与文件上传走明文通道
  if (config.responseType === "blob" || config.data instanceof FormData) {
    return config;
  }
  const aesKey = randomAesKey();
  const encryptedKey = rsaEncrypt(aesKey);
  // RSA 加密失败兜底：走明文，保证接口可用性（后端无加密头即按明文处理）
  if (!encryptedKey) {
    console.error("接口加密异常：RSA 加密会话密钥失败");
    return config;
  }
  config.headers["X-Api-Key"] = encryptedKey;
  config.headers["X-Api-Enc"] = "1";
  config.__apiAesKey = aesKey;
  // 报文体加密：字符串（qs.stringify 结果）直接加密；对象（未走 qs 的调用点）序列化后加密
  if (config.data !== undefined && config.data !== null) {
    const isStringData = typeof config.data === "string";
    const plain = isStringData ? config.data : JSON.stringify(config.data);
    config.data = aesEncrypt(plain, aesKey);
    if (!isStringData) {
      // 原本会由 axios 以 JSON 发送的报文，解密后仍是 JSON 串，声明给后端按 JSON 流处理
      config.headers["Content-Type"] = "application/json;charset=UTF-8";
    }
  }
  return config;
}

/**
 * 响应拦截器解密入口（main.js 的 axios.interceptors.response 中调用）
 *
 * <p>后端加密响应格式为 {"encData": "..."}，解密还原为明文 JSON 后替换 response.data，
 * 后续原有的 code 判断逻辑（-1000 跳登录等）不受影响。
 */
export function decryptResponse(response) {
  const resData = response.data;
  const aesKey = response.config && response.config.__apiAesKey;
  if (resData && resData.encData && aesKey) {
    try {
      const plain = aesDecrypt(resData.encData, aesKey);
      response.data = JSON.parse(plain);
    } catch (e) {
      // 解密失败保留原始报文并暴露错误，便于排查密钥不一致问题
      console.error("接口响应解密失败", e);
    }
  }
  return response;
}



// WEBPACK FOOTER //
// ./src/utils/apiCrypto.js